import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Calendar,
	ExternalLink,
	FileText,
	Loader2,
} from "lucide-react";
import { useState } from "react";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

export const Route = createFileRoute("/public/$slug/")({
	component: PublicSolicitationsListPage,
});

function PublicSolicitationsListPage() {
	const { slug } = Route.useParams();
	const trpc = useTRPC();
	const [typeFilter, setTypeFilter] = useState<string>("all");

	// Fetch buyer data directly (self-contained for SSR compatibility)
	const { data: buyer, isLoading: buyerLoading, error: buyerError } = useQuery(
		trpc.public.organizations.getBySlug.queryOptions({ slug })
	);

	// Query public solicitations (only when buyer is loaded)
	const { data: solicitationsData, isLoading: solicitationsLoading } = useQuery({
		...trpc.public.solicitations.list.queryOptions({
			buyerOrgId: buyer?.id ?? "",
			limit: 100,
			offset: 0,
		}),
		enabled: !!buyer?.id,
	});

	// Handle loading state
	if (buyerLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Handle error/not found state
	if (buyerError || !buyer) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<h1 className="text-2xl font-bold mb-2">Organization Not Found</h1>
					<p className="text-muted-foreground">
						The organization you're looking for doesn't exist.
					</p>
				</div>
			</div>
		);
	}

	const isLoading = solicitationsLoading;

	// Filter out drafts and apply type filter
	const solicitations = (solicitationsData ?? []).filter((s) => {
		if (s.status === "draft") return false;
		const matchesType = typeFilter === "all" || s.type === typeFilter;
		return matchesType;
	});

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center justify-between h-16">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
								<span className="text-primary-foreground font-bold">
									{buyer.name.charAt(0).toUpperCase()}
								</span>
							</div>
							<div>
								<h1 className="font-semibold text-lg">{buyer.name}</h1>
								<p className="text-xs text-muted-foreground">Procurement Portal</p>
							</div>
						</div>
						<div className="flex items-center gap-4">
							<Button variant="outline" asChild>
								<a href={`https://app.bidscope.ai/purchasing?slug=${slug}`}>
									Vendor Login
								</a>
							</Button>
						</div>
					</div>
				</div>
			</header>

			{/* Hero */}
			<div className="bg-primary text-primary-foreground py-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h1 className="text-4xl font-bold mb-4">{buyer.name}</h1>
					<p className="text-xl opacity-90">
						Procurement Opportunities Portal
					</p>
				</div>
			</div>

			{/* Content */}
			<main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Filters */}
				<div className="flex flex-col sm:flex-row gap-4 mb-6">
					<Select value={typeFilter} onValueChange={setTypeFilter}>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder="All Types" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Types</SelectItem>
							<SelectItem value="RFP">RFP</SelectItem>
							<SelectItem value="RFQ">RFQ</SelectItem>
							<SelectItem value="RFI">RFI</SelectItem>
							<SelectItem value="IFB">IFB</SelectItem>
							<SelectItem value="ITB">ITB</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-muted-foreground self-center">
						Showing {solicitations.length} opportunities
					</p>
				</div>

				{/* Solicitations Grid */}
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
					</div>
				) : (
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
						{solicitations.map((sol) => (
							<Card key={sol.id} className="hover:shadow-lg transition-shadow">
								<CardHeader>
									<div className="flex items-start justify-between">
										<Badge variant="outline">{sol.type}</Badge>
										<Badge className={getStatusColor(getEffectiveStatus(sol.status, sol.publish_date, sol.due_date))}>
											{getStatusLabel(getEffectiveStatus(sol.status, sol.publish_date, sol.due_date))}
										</Badge>
									</div>
									<CardTitle className="text-lg mt-2">
										<Link
											to="/public/$slug/solicitations/$solicitationId"
											params={{ slug: slug, solicitationId: sol.id }}
											className="hover:underline"
										>
											{sol.title}
										</Link>
									</CardTitle>
									<CardDescription className="line-clamp-2">
										{sol.description}
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-2 text-sm">
										<div className="flex items-center gap-2 text-muted-foreground">
											<Calendar className="h-4 w-4" />
											<span>
												Due: {new Date(sol.due_date).toLocaleDateString()}
											</span>
										</div>
										<div className="flex items-center gap-2 text-muted-foreground">
											<FileText className="h-4 w-4" />
											<span>{sol.type} Opportunity</span>
										</div>
									</div>
									<Button variant="outline" className="w-full mt-4" asChild>
										<Link
											to="/public/$slug/solicitations/$solicitationId"
											params={{ slug: slug, solicitationId: sol.id }}
										>
											View Details
											<ExternalLink className="ml-2 h-4 w-4" />
										</Link>
									</Button>
								</CardContent>
							</Card>
						))}
					</div>
				)}

				{!isLoading && solicitations.length === 0 && (
					<div className="text-center py-12">
						<FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
						<h3 className="text-lg font-medium">No solicitations found</h3>
						<p className="text-muted-foreground">
							{typeFilter !== "all"
								? "Try adjusting your filter"
								: "There are no active solicitations at this time"}
						</p>
					</div>
				)}
			</main>

			{/* Footer */}
			<footer className="bg-white border-t mt-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="grid gap-8 md:grid-cols-3">
						<div>
							<h3 className="font-semibold mb-3">{buyer.name}</h3>
							<p className="text-sm text-muted-foreground">
								{buyer.address ?? "Address not available"}
							</p>
						</div>
						<div>
							<h3 className="font-semibold mb-3">Contact</h3>
							<p className="text-sm text-muted-foreground">
								{buyer.phone && (
									<>
										Phone: {buyer.phone}
										<br />
									</>
								)}
								{buyer.email && <>Email: {buyer.email}</>}
								{!buyer.phone && !buyer.email && "Contact information not available"}
							</p>
						</div>
						<div>
							<h3 className="font-semibold mb-3">Resources</h3>
							<div className="space-y-1 text-sm">
								<Link
									to="/vendor/$buyerSlug"
									params={{ buyerSlug: slug }}
									className="block text-primary hover:underline"
								>
									Vendor Portal
								</Link>
							</div>
						</div>
					</div>
					<div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
						Powered by Bidscope - Government Procurement Platform
					</div>
				</div>
			</footer>
		</div>
	);
}
